import {
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import type { UIError } from "../../errors/types";
import type { components } from "../schema";

export type BranchPreviewRequest =
  components["schemas"]["BranchPreviewRequest"];
export type BranchPreviewResponse =
  components["schemas"]["BranchPreviewResponse"];

/** useBranchPreview.mutate({}) の引数。 */
export type BranchPreviewVariables = {
  worldlineId: string;
  sceneId: string;
} & BranchPreviewRequest;

/** useBranchPreview の error は UIError に正規化済み (ApiError → UIError 変換済み)。 */
export type UseBranchPreviewResult = UseMutationResult<
  BranchPreviewResponse,
  UIError,
  BranchPreviewVariables
>;

/**
 * worldline-palimpsest (Phase 3-1, Task 4.3) 未踏分岐 AI 提案 hook。
 *
 * on-demand mutation (useMutation) として実装する。長押し / 明示ボタン tap 時に
 * `mutate({ worldlineId, sceneId, choiceId, instruction? })` を呼び、成功時は
 * `BranchPreviewResponse` ({ summary, keywords, model, generatedAt }) を返す。
 *
 * エラーは `mapProblemDetail` 経由で `UIError` に変換される。
 * 特に `/errors/branch-preview-timeout` (504) と `/errors/branch-preview-failed` (503) は
 * `aiTimeout` 系の UIError にマップされ、UI 側は "?" を維持して再試行誘導する (Req 5.4, 12.4)。
 *
 * cache key に関して: mutation はデフォルトで cache しないため、同じ (worldlineId, sceneId,
 * choiceId) に対する 2 回目の呼び出しは再度 API を叩く。UI 側で下書き結果を一時保持したい
 * 場合は useState で保持する設計 (design.md §Data Flow 5min staleTime はハンドラ内では
 * 不要)。
 */
export function useBranchPreview(): UseBranchPreviewResult {
  const client = useApiClient();

  return useMutation<BranchPreviewResponse, UIError, BranchPreviewVariables>({
    mutationFn: async ({ worldlineId, sceneId, ...body }) => {
      try {
        const { data, error } = await client.POST(
          "/worldlines/{worldlineId}/scenes/{sceneId}/branch-preview",
          {
            params: { path: { worldlineId, sceneId } },
            body,
          },
        );
        if (error !== undefined) {
          // 通常 middleware が ApiError を throw するためここには到達しないが、
          // 型安全のための保険として明示的に throw する。
          throw new ApiError(
            typeof (error as { status?: number }).status === "number"
              ? (error as { status: number }).status
              : 0,
            error as unknown as ApiError["problem"],
          );
        }
        if (data === undefined) {
          throw new ApiError(500, {
            type: "about:blank",
            title: "Empty response",
            status: 500,
            detail: "postBranchPreview returned no data",
          });
        }
        return data;
      } catch (err: unknown) {
        // ApiError / TypeError / 未知例外を UIError に正規化してから throw。
        // Tanstack Query の error 型を UIError に固定することで、呼出側は ApiError を意識せずに済む。
        throw mapProblemDetail(err);
      }
    },
  });
}
